import { Platform, Share } from 'react-native';
import Constants from 'expo-constants';

/**
 * Base origin for shareable links, and for resolving site-relative asset
 * paths (e.g. a checklist brand's logoUrl). On web the current origin is
 * always right (localhost in dev, the hosting domain once deployed). On
 * native we read the per-environment `webOrigin` from app.config.ts extra
 * so dev builds emit dev-host links instead of the production domain
 * (which doesn't serve the app yet).
 */
export function siteBaseUrl(): string {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    return window.location.origin;
  }
  const origin = (Constants.expoConfig?.extra as { webOrigin?: string } | undefined)?.webOrigin;
  return origin ?? 'https://www.weareredi.net';
}

// Kept for existing callers in this file.
const shareBaseUrl = siteBaseUrl;

export function postShareUrl(postId: string): string {
  return `${shareBaseUrl()}/post/${postId}`;
}

export function structureShareUrl(structureId: string): string {
  return `${shareBaseUrl()}/structure/${structureId}`;
}

export function planShareUrl(planId: string): string {
  return `${shareBaseUrl()}/plan/${planId}`;
}

/** The public page for a plan event: sign-up, calendar, directions. No account needed. */
export function planEventShareUrl(eventId: string): string {
  return `${shareBaseUrl()}/event/${eventId}`;
}

/** The public page where a vendor says "I can help" on a bundle (Oct 2026). */
export function planBundleOpportunityUrl(planId: string, bundleId: string): string {
  return `${shareBaseUrl()}/opportunity/${encodeURIComponent(planId)}/${encodeURIComponent(bundleId)}`;
}

/**
 * Cross-platform share: React Native's Share API on iOS/Android,
 * `navigator.share` (then clipboard) on web. Returns 'copied' when the web
 * fallback wrote the link to the clipboard so callers can confirm to the
 * user, since a silent copy looks like the button did nothing. Returns
 * 'failed' when the clipboard write itself throws, so callers can tell the
 * user the copy did not happen — distinct from 'dismissed', which is a
 * touch user closing the native share sheet without picking anything and
 * should stay silent.
 */
async function shareUrl({
  url,
  title,
  text,
}: {
  url: string;
  title: string;
  text?: string;
}): Promise<'shared' | 'copied' | 'dismissed' | 'failed'> {
  const message = text?.trim() ? `${text}\n\n${url}` : url;

  if (Platform.OS === 'web') {
    const nav = globalThis.navigator as Navigator & {
      share?: (data: { title?: string; text?: string; url?: string }) => Promise<void>;
    };
    // Only touch devices get the system share sheet. Desktop Chrome and
    // Safari on macOS also expose navigator.share, and there a button
    // labeled "Copy link" opened the OS share menu instead of copying
    // (found on dev, Sept 25 2026). A mouse means copy.
    const coarsePointer =
      typeof globalThis.matchMedia === 'function' &&
      globalThis.matchMedia('(pointer: coarse)').matches;
    // Tracks whether the user already saw the native share sheet, so a
    // clipboard failure right after it (they likely just cancelled) stays
    // silent instead of piling an error toast on top.
    let sawShareSheet = false;
    if (nav.share && coarsePointer) {
      sawShareSheet = true;
      try {
        await nav.share({ title, text, url });
        return 'shared';
      } catch {
        // user cancelled or unsupported — fall through to clipboard
      }
    }
    if (nav.clipboard?.writeText) {
      try {
        await nav.clipboard.writeText(url);
        return 'copied';
      } catch {
        // No clipboard permission (e.g. the document lost focus).
        return sawShareSheet ? 'dismissed' : 'failed';
      }
    }
    return sawShareSheet ? 'dismissed' : 'failed';
  }

  await Share.share({ message, url });
  return 'shared';
}

interface SharePostOptions {
  postId: string;
  caption?: string;
}

/**
 * Share an observation. The link is a universal-link URL that opens the
 * in-app post detail screen — public for signed-out recipients.
 */
export async function sharePost({
  postId,
  caption,
}: SharePostOptions): Promise<'shared' | 'copied' | 'dismissed' | 'failed'> {
  return shareUrl({ url: postShareUrl(postId), title: 'REDI observation', text: caption });
}

/**
 * Invite a friend to REDI — shares the app's web origin itself rather than
 * a specific post or property (client plan: "prompt users to suggest other
 * users"). Phase 1 stub: no referral attribution yet.
 */
export async function shareAppInvite(): Promise<'shared' | 'copied' | 'dismissed' | 'failed'> {
  return shareUrl({
    url: shareBaseUrl(),
    title: 'Join me on REDI',
    text: "I'm using REDI to help our community prepare for wildfire. Take a look:",
  });
}

interface ShareStructureOptions {
  structureId: string;
  address?: string;
}

/**
 * Share a property's page — the read-only view of its observations that a
 * recipient without an account (e.g. an insurer) can open directly.
 */
export async function shareStructure({
  structureId,
  address,
}: ShareStructureOptions): Promise<'shared' | 'copied' | 'dismissed' | 'failed'> {
  return shareUrl({
    url: structureShareUrl(structureId),
    title: 'Property readiness on REDI',
    text: address,
  });
}

interface SharePlanOptions {
  planId: string;
  title?: string;
}

/**
 * Share a defense plan, the drawn strategies tied to one owner, so a
 * neighbor without an account can flip through it directly.
 */
export async function sharePlan({
  planId,
  title,
}: SharePlanOptions): Promise<'shared' | 'copied' | 'dismissed' | 'failed'> {
  return shareUrl({
    url: planShareUrl(planId),
    title: 'Defense plan on REDI',
    text: title,
  });
}

interface SharePlanEventOptions {
  eventId: string;
  title?: string;
}

/**
 * Share a bundle's opportunity page with vendors, who can bid on it without
 * an account. Same copy-or-share behavior as the other links.
 */
export async function shareBundleOpportunity({
  planId,
  bundleId,
  bundleName,
}: {
  planId: string;
  bundleId: string;
  bundleName: string;
}): Promise<'shared' | 'copied' | 'dismissed' | 'failed'> {
  return shareUrl({
    url: planBundleOpportunityUrl(planId, bundleId),
    title: 'Wildfire defense work on REDI',
    text: bundleName,
  });
}

/**
 * Share an event's public page so a neighbor can see the time and place and
 * sign up without an account. Same copy-or-share behavior as the other links.
 */
export async function sharePlanEvent({
  eventId,
  title,
}: SharePlanEventOptions): Promise<'shared' | 'copied' | 'dismissed' | 'failed'> {
  return shareUrl({
    url: planEventShareUrl(eventId),
    title: 'Community work day on REDI',
    text: title,
  });
}
